import React from "react";
import { ArrowUpRight } from "@phosphor-icons/react";
import { Reveal, SectionLabel } from "./Reveal";
import { useLanguage } from "@/i18n/LanguageContext";
import { Butterfly } from "./Brand";

// Compact 4:3 illustration for the Method card — central butterfly + 6 orbit nodes
const MethodArt = () => (
  <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-livon-mist via-white to-livon-blush border border-livon-line">
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="relative w-[80%] aspect-square">
        <div className="absolute inset-0 rounded-full border border-livon-ink/15" />
        <div className="absolute inset-[18%] rounded-full border border-dashed border-livon-ink/12" />
        <div className="absolute inset-[34%] rounded-full bg-white/80 backdrop-blur-[2px] border border-livon-line shadow-[0_15px_40px_-20px_rgba(126,111,184,0.35)]" />
        <div className="absolute inset-0 flex items-center justify-center">
          <Butterfly size={52} />
        </div>
        {Array.from({ length: 6 }).map((_, i) => {
          const angle = (i / 6) * Math.PI * 2 - Math.PI / 2;
          const r = 50;
          const x = 50 + r * Math.cos(angle);
          const y = 50 + r * Math.sin(angle);
          const colors = [
            "bg-livon-lavender",
            "bg-livon-blush",
            "bg-livon-cloud",
            "bg-livon-sand",
            "bg-livon-mist",
            "bg-livon-ink",
          ];
          return (
            <div
              key={i}
              className="absolute"
              style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)" }}
            >
              <div className={`w-2.5 h-2.5 rounded-full ${colors[i]} ring-[3px] ring-white/80`} />
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

// Compact 4:3 phone mockup for the App card
const AppArt = () => (
  <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-livon-cloud via-white to-livon-mist border border-livon-line">
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-[36%] aspect-[9/19] rounded-[1.5rem] bg-white border border-livon-line shadow-[0_20px_50px_-20px_rgba(21,19,26,0.3)] p-3 flex flex-col">
        <div className="flex items-center gap-1.5 mb-2">
          <Butterfly size={12} />
          <span className="serif text-livon-ink text-[10px]" style={{ fontWeight: 500 }}>
            Livon
          </span>
        </div>
        <div className="h-1 w-6 bg-livon-lavender rounded-full mb-1.5" />
        <div className="space-y-1 mt-0.5">
          <div className="h-1.5 w-full bg-livon-mist rounded-full" />
          <div className="h-1.5 w-3/4 bg-livon-blush rounded-full" />
          <div className="h-1.5 w-1/2 bg-livon-cloud rounded-full" />
        </div>
        <div className="mt-auto grid grid-cols-3 gap-0.5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="aspect-square rounded bg-livon-surface" />
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Compact 4:3 illustration for the Academy card — stacked pages/badge
const AcademyArt = () => (
  <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-livon-sand via-white to-livon-blush border border-livon-line">
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="relative w-[55%] aspect-[3/4]">
        {/* stacked sheets */}
        <div className="absolute inset-0 rounded-xl bg-white border border-livon-line shadow-[0_15px_30px_-15px_rgba(21,19,26,0.2)] translate-x-3 translate-y-3 rotate-[3deg]" />
        <div className="absolute inset-0 rounded-xl bg-white border border-livon-line shadow-[0_15px_30px_-15px_rgba(21,19,26,0.2)] -translate-x-1.5 translate-y-1.5 -rotate-[2deg]" />
        <div className="absolute inset-0 rounded-xl bg-white border border-livon-line shadow-[0_20px_45px_-15px_rgba(21,19,26,0.25)] p-4 flex flex-col">
          <div className="text-[8px] tracking-[0.3em] uppercase text-livon-muted font-medium">
            Practitioner
          </div>
          <div className="mt-2 h-1 w-10 bg-livon-lavender rounded-full" />
          <div className="mt-3 space-y-1">
            <div className="h-1 w-full bg-livon-surface rounded-full" />
            <div className="h-1 w-4/5 bg-livon-surface rounded-full" />
            <div className="h-1 w-3/5 bg-livon-surface rounded-full" />
          </div>
          <div className="mt-auto flex items-center justify-between">
            <Butterfly size={18} />
            <div className="w-5 h-5 rounded-full bg-livon-blush" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const OfferingCard = ({ id, label, title, body, cta, href, art, testIdPrefix }) => (
  <div
    id={id}
    data-testid={`${testIdPrefix}-card`}
    className="group flex flex-col h-full rounded-3xl bg-livon-surface/60 border border-livon-line p-6 md:p-8 transition-all duration-500 hover:-translate-y-1 hover:bg-white/80"
  >
    {art}
    <div className="mt-7 md:mt-8 flex flex-col flex-1">
      <div
        className="text-[11px] tracking-[0.3em] uppercase text-livon-muted font-medium"
        data-testid={`${testIdPrefix}-label`}
      >
        {label}
      </div>
      <h3
        className="mt-3 serif text-livon-ink leading-[1.1] tracking-tight"
        style={{ fontSize: "clamp(1.5rem, 1.9vw, 1.85rem)", fontWeight: 500 }}
        data-testid={`${testIdPrefix}-title`}
      >
        {title}
      </h3>
      <p
        className="mt-4 text-livon-muted text-sm md:text-[15px] leading-relaxed"
        data-testid={`${testIdPrefix}-body`}
      >
        {body}
      </p>
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        data-testid={`${testIdPrefix}-cta`}
        className="mt-auto pt-6 inline-flex items-center gap-2 text-livon-ink text-[12px] tracking-[0.22em] uppercase font-medium border-b border-livon-ink pb-1 self-start hover:text-livon-lavender hover:border-livon-lavender transition-colors"
      >
        <span>{cta}</span>
        <ArrowUpRight size={14} weight="light" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </a>
    </div>
  </div>
);

export const Offerings = () => {
  const { t } = useLanguage();
  const o = t.offerings;
  return (
    <section
      id="offerings"
      data-testid="offerings-section"
      className="relative py-28 md:py-40 bg-livon-bg"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-16 md:mb-20">
          <Reveal>
            <SectionLabel testId="offerings-label">{o.label}</SectionLabel>
          </Reveal>
          <Reveal delay={0.05}>
            <h2
              className="serif text-livon-ink leading-[1.04] tracking-tight"
              style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)", fontWeight: 500 }}
              data-testid="offerings-title"
            >
              {o.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p
              className="mt-6 text-livon-muted text-base md:text-lg leading-relaxed max-w-2xl"
              data-testid="offerings-lead"
            >
              {o.lead}
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 auto-rows-fr">
          <Reveal delay={0.05}>
            <OfferingCard
              id="method"
              label={t.method.label}
              title={t.method.title}
              body={t.method.body}
              cta={t.method.cta}
              href="https://method.livon.se"
              art={<MethodArt />}
              testIdPrefix="method"
            />
          </Reveal>
          <Reveal delay={0.12}>
            <OfferingCard
              id="app"
              label={t.appSec.label}
              title={t.appSec.title}
              body={t.appSec.body}
              cta={t.appSec.cta}
              href="https://app.livon.se"
              art={<AppArt />}
              testIdPrefix="app"
            />
          </Reveal>
          <Reveal delay={0.19}>
            <OfferingCard
              id="education"
              label={t.education.label}
              title={t.education.title}
              body={t.education.body}
              cta={t.education.cta}
              href="https://academy.livon.se"
              art={<AcademyArt />}
              testIdPrefix="education"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
